import { useFlueAgent } from '@flue/react';
import { useMemo, useState } from 'react';
import { PresenceBar, label, useMessageAuthors, usePresence } from './StepPresence.tsx';

/**
 * Chat with one step.
 *
 * The conversation URL is the whole contract — `/api/agents/step/<runId>--<stepId>`
 * — so this component needs no knowledge of the run beyond that id, and opening
 * a step from three days ago is the same code path as opening a live one.
 *
 * Scoping is real, not cosmetic: this is that step's own agent context. It cannot
 * see its siblings' conversations, and its replies come from the model with that
 * step's tools, policy, and connections mounted.
 *
 * **It is also multiplayer, which is why there is a claim before every send.**
 * Several people can have this step open — the conversation is shared, the
 * authority is not (ARCHITECTURE.md §24) — and a prompt that arrived with no
 * sender attached left the step acting as whoever started the run. So the
 * sender is recorded first and the message is sent second: the claim is what a
 * commit's author is derived from, and sending ahead of it would credit the
 * work to the previous speaker. A claim that is refused therefore stops the
 * send rather than being shrugged off — `edit` on the workflow is the bar for
 * both, so a refused claim is a send that was going to be refused anyway.
 */
export function StepChat({
  conversationId,
  stepName,
  onFork,
}: {
  conversationId: string;
  stepName: string;
  onFork(): void;
}) {
  const agent = useFlueAgent({ url: `/api/agents/step/${encodeURIComponent(conversationId)}` });
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);

  const { presence, presenceError, claimTurn } = usePresence(conversationId);

  /*
   * The user messages in order, which is the sequence a claim's ordinal counts
   * against. Derived from the transcript rather than counted as we send, so a
   * reader who joined halfway through numbers the messages the same way the
   * person who sent them did.
   */
  const userMessages = useMemo(
    () =>
      agent.messages
        .filter((message) => message.role === 'user')
        .map((message) =>
          message.parts
            .map((part) => (part.type === 'text' ? part.text : ''))
            .join(''),
        ),
    [agent.messages],
  );

  const authors = useMessageAuthors(userMessages, presence?.attributions ?? []);

  /*
   * Which user message each transcript row is, by index. The transcript
   * interleaves the step's turns with people's, and a claim counts only the
   * people's — so the two sequences have to be mapped rather than assumed
   * equal, or every label after the step's first reply is off by one.
   */
  const userOrdinals = useMemo(() => {
    let ordinal = 0;
    return agent.messages.map((message) => (message.role === 'user' ? ordinal++ : -1));
  }, [agent.messages]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;

    /*
     * Claim first, and let a refusal stop the send. The claim is the only
     * record of who sent this message — the transcript itself has no sender —
     * so a message sent without one is a message the step will act on under the
     * previous speaker's identity.
     */
    try {
      await claimTurn(text, userMessages.length);
    } catch (caught) {
      setSendError(`Not sent: ${(caught as Error).message}`);
      return;
    }

    setSendError(null);
    setDraft('');
    await agent.sendMessage(text);
  };

  const status = latestStatus(agent.messages);
  const canSpeak = presence?.canSpeak ?? true;

  return (
    <div className="chat">
      <div className="row chat-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="chat-title">Chat with {stepName}</p>
          <code className="chat-address">{conversationId}</code>
          <div>
            <PresenceBar presence={presence} error={presenceError} />
          </div>
        </div>
        {status && (
          <span className={`pill ${status.status === 'failed' ? 'danger' : 'accent'}`}>
            {status.status} · ${(status.spendCents / 100).toFixed(2)}
          </span>
        )}
        <button className="ghost" onClick={onFork} title="Start a new run from this point">
          Fork
        </button>
      </div>

      <div className="chat-log">
        {agent.messages.length === 0 && (
          <p className="hint">
            Nothing yet. Anything you send here goes to this step alone — it does not see the other
            steps' conversations.
          </p>
        )}

        {/*
          A transcript with speakers, not a stack of chat bubbles. A bubble says
          "two peers talking"; this is a record of a step doing work, so every
          turn starts in the same column and the author line is what you scan.
        */}
        {agent.messages.map((message, messageIndex) => {
          /*
           * The author line, which is the line you scan. A user message shows
           * who sent it when a claim matches it, and plainly says "user" when
           * none does — a message from before attribution existed, or one whose
           * claim lost a race. Naming a guess here would be worse than the gap:
           * this is the same record a commit's author is taken from.
           */
          const author = authors.get(userOrdinals[messageIndex] ?? -1);
          const speaker =
            message.role === 'user'
              ? author
                ? label({ email: author.senderEmail, name: null, userId: author.senderId, you: author.you })
                : 'user'
              : message.role;

          return (
          <div key={messageIndex} className={`msg ${message.role}`}>
            <div
              className="role"
              title={
                message.role === 'user' && !author
                  ? 'Reef has no record of who sent this message.'
                  : undefined
              }
            >
              <span className="msg-marker" aria-hidden="true">
                {message.role.slice(0, 1)}
              </span>
              {speaker}
            </div>
            <div className="msg-text">
              {message.parts.map((part, partIndex) => {
                if (part.type === 'text') return <span key={partIndex}>{part.text}</span>;
                // Status is rendered in the header rather than inline; other data
                // parts are shown raw so nothing the step emits is silently lost.
                if (part.type === 'data-stepStatus') return null;
                if (part.type.startsWith('data-')) {
                  return (
                    <pre key={partIndex}>{JSON.stringify((part as { data: unknown }).data, null, 2)}</pre>
                  );
                }
                return null;
              })}
            </div>
          </div>
          );
        })}
      </div>

      {/*
        One field, not a field and a button beside it. The send affordance lives
        inside the border you are typing into, so the composer reads as a single
        control — and the keyboard shortcut is stated where it is used.
      */}
      <div className="chat-input">
        <div className="composer">
          <textarea
            rows={3}
            value={draft}
            disabled={!canSpeak}
            placeholder="Ask this step something, or tell it what to do next…"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                void send();
              }
            }}
          />
          <div className="composer-foot">
            {/*
              What the composer says about itself, in one place. A reader who may
              not send is told so instead of being handed a Send that 403s — the
              studio does not offer a control the API would refuse — and a
              refused claim says the message was not sent, because a draft
              silently cleared on a failure is a message somebody thinks they
              sent.
            */}
            <span className="hint">
              {sendError ?? (canSpeak ? '⌘↵ to send' : 'You can follow this step but not send to it')}
            </span>
            <span className="spacer" />
            <button
              className="primary"
              onClick={() => void send()}
              disabled={!draft.trim() || !canSpeak}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface StepStatusData {
  status: string;
  spendCents: number;
  maxSpendCents: number;
}

/** The step writes its status on a data channel; take the most recent one. */
function latestStatus(
  messages: ReadonlyArray<{ parts: ReadonlyArray<{ type: string; data?: unknown }> }>,
): StepStatusData | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const parts = messages[i]!.parts;
    for (let j = parts.length - 1; j >= 0; j--) {
      const part = parts[j]!;
      if (part.type === 'data-stepStatus' && part.data) return part.data as StepStatusData;
    }
  }
  return null;
}
